import * as React from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Box, useTheme, Fade, Zoom } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";

interface Props {
  image: string;
  title: string;
  description: string;
  action: () => void;
}

export const CardHomePage = ({
  card: { image, title, description, action },
}: {
  card: Props;
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  return (
    <Zoom in={true} timeout={600}>
      <Card
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          maxWidth: 345,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: `linear-gradient(135deg, 
            rgba(255, 255, 255, 0.95) 0%, 
            rgba(255, 255, 255, 0.9) 100%)`,
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          border: `2px solid rgba(90, 100, 119, 0.1)`,
          boxShadow: `0 8px 32px rgba(90, 100, 119, 0.15)`,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-12px) scale(1.02)",
            boxShadow: `0 20px 60px rgba(90, 100, 119, 0.25)`,
            border: `2px solid ${theme.palette.secondary.main}`,
            "& .card-media": {
              transform: "scale(1.1)",
            },
            "& .card-content": {
              "& .MuiTypography-h5": {
                color: theme.palette.secondary.main,
              },
            },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, 
              ${theme.palette.secondary.main} 0%, 
              ${theme.palette.primary.main} 100%)`,
            transform: isHovered ? "scaleX(1)" : "scaleX(0)",
            transformOrigin: "left",
            transition: "transform 0.3s ease",
          },
        }}
      >
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <CardMedia
            component="img"
            alt={title}
            height="200"
            image={image}
            className="card-media"
            onLoad={() => setImageLoaded(true)}
            sx={{
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: "rgba(240, 240, 240, 0.5)",
              objectFit: "cover",
              opacity: imageLoaded ? 1 : 0,
            }}
          />
          {!imageLoaded && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(240, 240, 240, 0.8)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(
                180deg, 
                transparent 0%, 
                rgba(90, 100, 119, 0.1) 100%
              )`,
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
        </Box>

        <CardContent
          className="card-content"
          sx={{
            flexGrow: 1,
            padding: "20px",
            background: "transparent",
          }}
        >
          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: "1.4rem",
              color: theme.palette.primary.main,
              transition: "color 0.3s ease",
              marginBottom: "12px",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(90, 100, 119, 0.8)",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>
        </CardContent>

        <CardActions
          sx={{
            padding: "16px 20px 20px",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Fade in={isHovered} timeout={300}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                }}
              >
                Click to explore
              </Typography>
            </Box>
          </Fade>
          <Button
            size="medium"
            variant="contained"
            endIcon={<LaunchIcon />}
            onClick={action}
            sx={{
              borderRadius: "12px",
              padding: "10px 20px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9rem",
              background: `linear-gradient(135deg, 
                ${theme.palette.secondary.main} 0%, 
                rgba(251, 171, 53, 0.8) 100%)`,
              boxShadow: `0 4px 16px rgba(251, 171, 53, 0.3)`,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: `linear-gradient(135deg, 
                  rgba(251, 171, 53, 0.9) 0%, 
                  ${theme.palette.secondary.main} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 8px 24px rgba(251, 171, 53, 0.4)`,
              },
              "&:active": {
                transform: "translateY(0px)",
              },
            }}
          >
            Start
          </Button>
        </CardActions>
      </Card>
    </Zoom>
  );
};
